// THE BOARD STAYS WHERE IT WAS. The server ranks groups on every load (work first, then
// overdue, then money), and every save reloads — so marking a client's last task done
// dropped that client far down the page and slid another one into the spot being read.
//
// So the order is taken ONCE per view — when the screen opens, or when a filter changes
// (`resetKey`) — and every reload after that keeps it:
//   - a group already on screen keeps its place, whatever its new rank;
//   - a group that is new to the view is placed where the server ranked it;
//   - a group that VANISHED after one of its tasks was marked done (`keep(key)` — the
//     "mine" view or the urgency filter drops a group whose last matching task is gone)
//     stays in place with no tasks, until the next visit; `forget(key)` lets a group go
//     that really left (a lead deleted, converted or lost).
// `firstSeen(key)` is whatever `seedOf` said about the group the first time this view
// saw it — the board uses it for the fold, so a group never folds by itself after a save.
import { useMemo, useRef, useState } from 'react';

// Pure, so the rule can be tested without a screen. `prev` is the order on screen
// (keys), `groups` the fresh rows in the server's order, `kept` the keys to hold on to
// if they vanish, `last` the most recent group seen for each key.
export const stableOrder = ({ prev, groups, keyOf, kept, last }) => {
  const fresh = new Map(groups.map((g) => [keyOf(g), g]));
  const order = prev.filter((k) => fresh.has(k) || (kept.has(k) && last.has(k)));
  const placed = new Set(order);
  groups.forEach((g, i) => {
    const k = keyOf(g);
    if (!placed.has(k)) { order.splice(Math.min(i, order.length), 0, k); placed.add(k); }
  });
  return order.map((k) => fresh.get(k) || { ...last.get(k), tasks: [] });
};

export default function useStableOrder(groups, { keyOf, resetKey, seedOf = () => undefined }) {
  const memo = useRef({ resetKey: undefined, order: [], last: new Map(), kept: new Set(), seen: new Map() });
  const [, setVersion] = useState(0);

  const ordered = useMemo(() => {
    const m = memo.current;
    if (groups == null) return null;
    if (m.resetKey !== resetKey) {
      m.resetKey = resetKey;
      m.order = [];
      m.kept = new Set();
      m.seen = new Map();
    }
    const out = stableOrder({ prev: m.order, groups, keyOf, kept: m.kept, last: m.last });
    m.order = out.map(keyOf);
    for (const g of groups) {
      const k = keyOf(g);
      m.last.set(k, g);
      if (!m.seen.has(k)) m.seen.set(k, seedOf(g));
    }
    return out;
    // keyOf / seedOf are pure helpers; the order follows the data and the view only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, resetKey]);

  const keep = (key) => { memo.current.kept.add(key); };
  const forget = (key) => {
    memo.current.kept.delete(key);
    memo.current.order = memo.current.order.filter((k) => k !== key);
    setVersion((v) => v + 1);
  };
  const firstSeen = (key) => memo.current.seen.get(key);

  return { ordered, keep, forget, firstSeen };
}
